'use client';

export interface DateFormatOptions {
  separator?: '-' | '.';
  locale?: string;
  order?: 'ymd' | 'dmy' | 'mdy';
}

/**
 * Formats a Date object into a string with customizable separator and order.
 * Uses Intl.DateTimeFormat for accurate locale-specific date ordering.
 *
 * @param date - The Date object to format
 * @param options - Configuration options for formatting
 * @param options.separator - Character to separate date parts ('-' or '.')
 * @param options.locale - Locale string (e.g., 'en-US', 'en-GB', 'de-DE') for automatic order detection
 * @param options.order - Explicit order override ('ymd', 'dmy', 'mdy')
 * @returns Formatted date string
 *
 * @remarks Return type is intentionally plain `string` (locale / separator variants). It is **not**
 * a `DateScalar`: do not pass this output to Relay as a `Date` variable without converting through
 * `dateScalarFromValidYmdString` (only when the string is strictly `YYYY-MM-DD`) or another trusted constructor from `./graphqlScalars`.
 *
 * @example
 * const date = new Date('2024-03-15');
 *
 * formatDate(date) // '2024-03-15' (default YMD)
 * formatDate(date, { separator: '.' }) // '2024.03.15'
 * formatDate(date, { locale: 'en-GB' }) // '15-03-2024' (UK format)
 * formatDate(date, { locale: 'en-US', separator: '.' }) // '03.15.2024' (US format)
 * formatDate(date, { order: 'dmy', separator: '.' }) // '15.03.2024'
 */
export function formatDateToYmd(
  date: Date,
  options: DateFormatOptions = {},
): string {
  const { separator = '-', locale, order } = options;

  // Get date components
  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, '0');

  const day = String(date.getDate()).padStart(2, '0');

  // Determine order based on explicit order or locale
  let dateOrder: 'ymd' | 'dmy' | 'mdy';

  if (order) {
    dateOrder = order;
  } else if (locale) {
    dateOrder = getLocaleOrderUsingIntl(locale);
  } else {
    dateOrder = 'ymd'; // Default to ISO format
  }

  // Format based on determined order
  switch (dateOrder) {
    case 'ymd':
      return `${year}${separator}${month}${separator}${day}`;

    case 'dmy':
      return `${day}${separator}${month}${separator}${year}`;

    case 'mdy':
      return `${month}${separator}${day}${separator}${year}`;

    default:
      return `${year}${separator}${month}${separator}${day}`;
  }
}

/**
 * Determines the date order for a given locale using Intl.DateTimeFormat.
 * This provides accurate locale-specific formatting by analyzing the actual
 * order that the browser's internationalization API uses.
 */
function getLocaleOrderUsingIntl(locale: string): 'ymd' | 'dmy' | 'mdy' {
  try {
    // Create a formatter for the given locale
    const formatter = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    // Use a test date to determine the order
    const testDate = new Date('2024-03-15'); // Year=2024, Month=03, Day=15

    const parts = formatter.formatToParts(testDate);

    // Get the order of year, month, day parts
    const datePartTypes = parts
      .filter((part) => ['year', 'month', 'day'].includes(part.type))
      .map((part) => part.type);

    // Determine the order based on the sequence
    const orderString = datePartTypes.join('');

    switch (orderString) {
      case 'yearmonthday':
        return 'ymd';

      case 'daymonthyear':
        return 'dmy';

      case 'monthbdayyear':
        return 'mdy';

      default:
        // Fallback: if we can't determine, default to YMD
        return 'ymd';
    }
  } catch (_error) {
    // If there's an error with the locale, default to YMD
    console.warn(`Invalid locale "${locale}", defaulting to YMD format`);

    return 'ymd';
  }
}

/**
 * Alternative function that directly uses Intl.DateTimeFormat for formatting.
 * This approach lets the browser handle both the ordering and formatting completely.
 *
 * @param date - The Date object to format
 * @param options - Configuration options for formatting
 * @returns Formatted date string using browser's native locale formatting
 *
 * @example
 * const date = new Date('2024-03-15');
 *
 * formatDateWithIntl(date) // '03/15/2024' (US default)
 * formatDateWithIntl(date, { locale: 'en-GB' }) // '15/03/2024'
 * formatDateWithIntl(date, { locale: 'de-DE' }) // '15.03.2024'
 * formatDateWithIntl(date, { locale: 'ja-JP' }) // '2024/03/15'
 * formatDateWithIntl(date, { separator: '-', locale: 'en-GB' }) // '15-03-2024'
 */
export function formatDateWithIntl(
  date: Date,
  options: DateFormatOptions = {},
): string {
  const { separator = '-', locale = 'en-US' } = options;

  try {
    // Use Intl.DateTimeFormat to get locale-appropriate formatting
    const formatter = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const parts = formatter.formatToParts(date);

    // Extract only the date parts in their locale-specific order
    const orderedParts = parts
      .filter((part) => ['year', 'month', 'day'].includes(part.type))
      .map((part) => part.value);

    return orderedParts.join(separator);
  } catch (_error: unknown) {
    // Fallback to basic formatting if locale is invalid
    console.warn(`Invalid locale "${locale}", using fallback formatting`);

    const year = date.getFullYear();

    const month = String(date.getMonth() + 1).padStart(2, '0');

    const day = String(date.getDate()).padStart(2, '0');

    return `${year}${separator}${month}${separator}${day}`;
  }
}

/**
 * Utility function to detect what date order a locale uses.
 * Useful for debugging or understanding locale behavior.
 *
 * @param locale - The locale to analyze
 * @returns Object containing the detected order and example formatting
 */
export function detectLocaleOrder(locale: string): {
  order: 'ymd' | 'dmy' | 'mdy';
  example: string;
  nativeExample: string;
} {
  const testDate = new Date('2024-03-15');

  const order = getLocaleOrderUsingIntl(locale);

  const example = formatDateToYmd(testDate, { locale, separator: '-' });

  // Show how the locale naturally formats (with its default separators)
  const nativeFormatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const nativeExample = nativeFormatter.format(testDate);

  return {
    order,
    example,
    nativeExample,
  };
}
